import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = location.pathname.startsWith("/admin");
  const isGallery = location.pathname === "/gallery";

  // Define the steps in the meme creation process
  const steps = [
    { path: "/", name: "הכנס שם", step: 1 },
    { path: "/", name: "בחר תבנית", step: 2 },
    { path: "/editor", name: "צור מימ", step: 3 },
    { path: "/done", name: "הושלם", step: 4 },
  ];

  // Find current step based on path and state
  const getCurrentStep = () => {
    if (location.pathname.startsWith("/editor")) {
      return steps[2]; // צור מימ
    }
    if (location.pathname === "/done") {
      return steps[3]; // הושלם
    }
    if (location.pathname === "/") {
      // Check if we're in template selection phase by looking at URL params
      const urlParams = new URLSearchParams(location.search);
      const showTemplatePicker = urlParams.get("showTemplatePicker") === "true";

      if (showTemplatePicker) {
        return steps[1]; // בחר תבנית
      }
      return steps[0]; // הכנס שם
    }
    return steps[0];
  };

  const currentStep = getCurrentStep();

  const getProgressPercentage = () => {
    return (currentStep.step / steps.length) * 100;
  };

  const handleStepClick = (step: (typeof steps)[0]) => {
    // Don't allow clicking on future steps
    if (step.step > currentStep.step) {
      return;
    }

    // If we're in the editor and trying to go back, show confirmation
    if (
      location.pathname.startsWith("/editor") &&
      step.step < currentStep.step
    ) {
      const confirmed = window.confirm(
        "האם אתה בטוח שברצונך לעזוב? השינויים שעשית לא יישמרו."
      );
      if (!confirmed) {
        return;
      }
    }

    // Navigate based on step
    if (step.step === 1) {
      // Go to name entry
      navigate("/");
    } else if (step.step === 2) {
      // Go to template selection
      const currentName =
        new URLSearchParams(location.search).get("name") || "";
      navigate(
        `/?showTemplatePicker=true&name=${encodeURIComponent(currentName)}`
      );
    } else if (step.step === 3) {
      // This shouldn't be clickable as it's the current step
      return;
    } else if (step.step === 4) {
      // Go to done page
      navigate("/done");
    }
  };

  if (isAdmin) {
    return (
      <div className="min-h-screen bg-gray-900">
        <Outlet />
      </div>
    );
  }

  if (isGallery) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-yellow-50">
        {/* Header with Title - Gallery Version */}
        <div className="bg-white/70 backdrop-blur-sm border-b border-pink-100">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="text-center">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-pink-600 to-yellow-600 bg-clip-text text-transparent mb-4">
                מחולל המימז של עוז וטלי
              </h1>
              <h2 className="text-md text-gray-800 mb-4" dir="rtl">
                פה אפשר לראות את כל מה ששאר האורחים הכינו!
                <br />
                רוצים גם להכין מימז?{" "}
                <Link to="/" className="text-pink-600 hover:text-pink-700">
                  לחצו פה!
                </Link>
              </h2>
            </div>
          </div>
        </div>

        <main className="flex-1">
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-yellow-50">
      {/* Header with Title */}
      <div className="bg-white/70 backdrop-blur-sm border-b border-pink-100">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-pink-600 to-yellow-600 bg-clip-text text-transparent mb-4">
              מחולל המימז של עוז וטלי
            </h1>

            {/* Progress Bar */}
            <div className="max-w-lg mx-auto" dir="rtl">
              <div className="flex justify-between items-center mb-2">
                {steps.map((step) => (
                  <div
                    key={step.step}
                    className="flex flex-col items-center cursor-pointer"
                    onClick={() => handleStepClick(step)}
                  >
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-200 ${
                        step.step <= currentStep.step
                          ? "bg-gradient-to-r from-pink-600 to-yellow-600 text-white hover:from-pink-700 hover:to-yellow-700"
                          : "bg-gray-200 text-gray-500 cursor-not-allowed"
                      } ${
                        step.step < currentStep.step ? "hover:scale-110" : ""
                      }`}
                    >
                      {step.step}
                    </div>
                    <span
                      className={`text-xs mt-1 text-center transition-colors duration-200 ${
                        step.step <= currentStep.step
                          ? "text-gray-800"
                          : "text-gray-500"
                      } ${
                        step.step < currentStep.step
                          ? "hover:text-pink-600"
                          : ""
                      }`}
                    >
                      {step.name}
                    </span>
                  </div>
                ))}
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-pink-600 to-yellow-600 h-2 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${getProgressPercentage()}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <main className="flex-1">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
